import React from "react";
import { Link } from "react-router-dom";
import { Heart, GraduationCap, Users, Globe, Shield, Sparkles, Monitor } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Public Benefit Statement — central evidence document for the UK Charity
 * Commission CIO application. Written specifically against the Buddha's
 * Dhamma and Babasaheb Ambedkar's social-reform legacy. Renders in the
 * public site shell so it can be linked from the trustee declaration and
 * cited in the CIO governing document.
 */

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }),
};

const BENEFITS = [
  {
    icon: Monitor,
    title: "Charitable digital platform",
    body: "ABGN-UK has built its own digital platform as charitable infrastructure (not as a commercial product). The platform delivers live meditation, Buddhist teachings, Dhamma talks, community meetings, structured educational courses, youth learning programmes, volunteer collaboration and study circles. It removes geographical, mobility, financial and caring-responsibility barriers that would otherwise exclude many beneficiaries from public participation in the Sangha.",
  },
  {
    icon: GraduationCap,
    title: "Education for every age",
    body: "Free Dhamma study materials, KS1–KS4 curriculum-linked workbooks for British schools, multilingual newsletter and an AI Dhamma assistant accessible 24/7. Members include first-generation Ambedkarite Buddhists who grew up without access to structured Buddhist education.",
  },
  {
    icon: Heart,
    title: "Mental wellbeing through Dhamma",
    body: "Weekly meditation sessions, a meditation timer with streak tracking, ambient music for calming practice, and live-broadcast Dhamma talks. The Buddha's framework of mindfulness, ethical conduct and compassion is offered as a non-clinical wellbeing resource open to anyone, regardless of faith.",
  },
  {
    icon: Users,
    title: "Inclusion, equality, social cohesion",
    body: "ABGN-UK serves Britain's Ambedkarite Buddhist community across faith, caste, gender, age and ability lines. Membership is non-discriminatory; volunteer roles are open; community discussion forums are moderated for safety. We expressly oppose caste-based and other discrimination.",
  },
  {
    icon: Globe,
    title: "Global Sangha networking",
    body: "Cloud-based meetings, livestreams and a translated newsletter (11 languages) connect members in the UK with Ambedkarite Buddhist communities in India, USA, Japan, Sri Lanka, Nepal and beyond, strengthening cultural exchange and shared learning.",
  },
  {
    icon: Shield,
    title: "Safeguarding and accessibility",
    body: "We follow a written safeguarding policy (covering children, vulnerable adults and online conduct), an equality & diversity statement, and an elder-friendly readable design (large fonts, high contrast, multilingual). Donor information is processed under UK GDPR.",
  },
  {
    icon: Sparkles,
    title: "Volunteer development and youth engagement",
    body: "Members can register as volunteers across teaching support, event organisation, content translation, technology and pastoral care. A youth wing is being developed to engage 13–25 year olds. Volunteer milestones and certificates recognise community contribution.",
  },
];

const ACTIVITIES = [
  "Weekly online Dhamma study circles open to non-members",
  "Quarterly in-person meetings at UK community venues (Stockport, Birmingham, London)",
  "Annual Buddha Purnima celebration",
  "Multilingual newsletter (Marathi, Hindi, Punjabi, Gujarati, Bengali, Tamil and 5 more)",
  "Children's reading programme aligned with KS1–KS4 curriculum",
  "Volunteer-led translation of Babasaheb Ambedkar's writings",
  "Free meditation room with streak tracking and ambient practice support",
  "AI Dhamma assistant providing teachings on demand, 24/7",
  "Public discussion forums on Dhamma, ethics, history and contemporary issues",
];

const NOT_FOR_PROFIT = [
  "Membership fees and donations are reinvested entirely into community activities and platform costs.",
  "No member, director or volunteer receives personal profit, dividend or share of any surplus.",
  "Surpluses (if any) are retained for charitable / community-interest activities such as scholarships, retreats and outreach.",
  "Asset lock: on dissolution, residual assets transfer only to another asset-locked body with similar charitable / community-interest purpose.",
];

export default function PublicBenefit() {
  return (
    <div className="bg-pearl">
      <section className="bg-gradient-to-b from-navy via-navy to-navy-light text-pearl">
        <div className="section-container py-20 sm:py-28">
          <p className="eyebrow text-saffron">Public Benefit Statement</p>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl"
          >
            Why ABGN-UK exists, and how the public benefits.
          </motion.h1>
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="mt-6 text-lg text-pearl/80 max-w-3xl leading-relaxed"
          >
            The Ambedkarite Buddhist Global Network UK is a non-profit community organisation
            uniting British members in the spirit of Babasaheb Dr. B. R. Ambedkar's
            embrace of the Buddha's Dhamma. This page sets out the tangible public
            benefit ABGN-UK provides, in plain language.
          </motion.p>
          <p className="mt-4 text-sm text-pearl/55 max-w-3xl">
            Last updated: February 2026 · Reviewed annually by the volunteer governance committee.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">Six pillars of public benefit</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">
              Concrete, measurable, open to everyone.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="bg-white border border-navy/10 p-7 rounded-sm shadow-sm"
                  data-testid={`benefit-${i}`}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-saffron/15 text-saffron mb-5">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-2xl text-navy">{b.title}</h3>
                  <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">{b.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow text-saffron">Activities</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">What we actually do.</h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              All activities are open, free or low-cost, and designed to deliver the
              educational, spiritual and community benefits described above.
            </p>
            <Link
              to="/events"
              data-testid="pb-events-link"
              className="mt-6 inline-flex items-center gap-2 text-navy border-b border-navy/30 hover:border-navy pb-1"
            >
              See upcoming events →
            </Link>
          </div>
          <ul className="lg:col-span-7 space-y-3 text-slate-700">
            {ACTIVITIES.map((a) => (
              <li key={a} className="flex items-start gap-3 border-l-2 border-saffron/40 pl-4 py-1">
                <span className="font-display text-saffron mt-0.5">•</span>
                <span className="leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="pb-platform-section">
        <div className="section-container max-w-5xl">
          <p className="eyebrow text-saffron">Platform-based charitable delivery</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">
            Removing the barriers that keep people out.
          </h2>
          <p className="mt-5 text-slate-700 leading-relaxed max-w-3xl">
            The Charity Commission's published guidance recognises that a charity may
            deliver its objects through digital means where this widens access and
            improves public benefit. ABGN-UK's digital platform is built precisely for
            that purpose &mdash; it is charitable mission infrastructure, not a
            commercial software business.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pb-barriers">
            {[
              { t: "Distance across UK cities", b: "Members from Stockport to London, Glasgow to Cardiff can sit in the same meditation together." },
              { t: "Disability and mobility", b: "Live captioning, screen-reader support, and the ability to join from a bed or wheelchair widen access." },
              { t: "Caring responsibilities", b: "Parents, carers and shift workers can attend Dhamma teaching without leaving the house." },
              { t: "Elderly participation", b: "Large fonts, slow pacing, and an interface designed for first-time digital users." },
              { t: "People without a local centre", b: "Most UK cities do not have an Ambedkarite Buddhist centre. The platform brings one to every postcode." },
              { t: "Financial access barriers", b: "Free baseline membership, hardship waivers on all paid tiers, and zero cost to attend public events on the platform." },
              { t: "Working family flexibility", b: "Sessions are recorded (with consent) so working families can catch up at any hour." },
              { t: "Hybrid participation", b: "Every public event runs simultaneously in-person and via the platform, so no one is left behind." },
            ].map((x) => (
              <div key={x.t} className="border border-navy/10 rounded-sm bg-pearl p-5">
                <div className="font-display text-base text-navy">{x.t}</div>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{x.b}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-slate-500 italic max-w-3xl">
            The platform is wholly owned by the CIO and is operated by volunteer
            trustees and members. It is not licensed to, leased to, or sold to any
            third party and generates no commercial income. Its sole purpose is the
            delivery of the CIO's charitable objects.
          </p>
        </div>
      </section>

      <section className="py-20 bg-pearl">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow text-gold">Not-for-profit structure</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">
              Every penny goes back to the community.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              ABGN-UK is constituted on a clear non-profit, asset-locked basis. The
              following commitments are written into our governance:
            </p>
          </div>
          <ul className="lg:col-span-7 space-y-4 text-slate-700">
            {NOT_FOR_PROFIT.map((p, i) => (
              <li key={i} className="bg-white border border-navy/10 rounded-sm p-5 leading-relaxed">
                <span className="font-display text-saffron mr-2">{i + 1}.</span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 bg-navy text-pearl">
        <div className="section-container max-w-3xl text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-pearl">Read more</h2>
          <p className="mt-4 text-pearl/75">
            For the full picture of our governance, please see our supporting policies.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/safeguarding" className="px-5 py-2.5 bg-saffron/15 border border-saffron/40 text-saffron rounded-sm text-sm hover:bg-saffron/25" data-testid="pb-safeguarding-link">
              Safeguarding policy
            </Link>
            <Link to="/equality-diversity" className="px-5 py-2.5 bg-saffron/15 border border-saffron/40 text-saffron rounded-sm text-sm hover:bg-saffron/25" data-testid="pb-eandd-link">
              Equality & diversity
            </Link>
            <Link to="/impact" className="px-5 py-2.5 bg-saffron/15 border border-saffron/40 text-saffron rounded-sm text-sm hover:bg-saffron/25" data-testid="pb-impact-link">
              Community impact
            </Link>
            <Link to="/volunteer" className="px-5 py-2.5 bg-pearl text-navy rounded-sm text-sm hover:bg-pearl/90" data-testid="pb-volunteer-link">
              Volunteer with us →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
